function escapeHtml (value) {
	return value
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#039;')
	;
}

function highlightMatch (label, query) {
	if (! query) {
		return escapeHtml (label);
	}

	const lowerLabel = label.toLowerCase ();
	const lowerQuery = query.toLowerCase ();
	const index = lowerLabel.indexOf (lowerQuery);

	if (-1 === index) {
		return escapeHtml (label);
	}

	const before = escapeHtml (label.slice (0, index));
	const match = escapeHtml (label.slice (index, index + query.length));
	const after = escapeHtml (label.slice (index + query.length));

	return `${before}<strong>${match}</strong>${after}`;
}

/*
	Tracks the currently-live forms' own clearResults()/form pairs so the
	single document-level click listener below (registered once, at module
	scope) can always close whichever forms are actually live right now -
	rather than each call to heroHomeSearch() adding its own new listener,
	which would leak one per ACF block-preview re-render (see the reinit hook
	at the bottom of this file).
*/
let activeForms = [];

document.addEventListener ('click', (event) => {
	activeForms.forEach (({ form, clearResults }) => {
		if (! form.contains (event.target)) {
			clearResults ();
		}
	});
});

export default function heroHomeSearch () {

	const forms = document.querySelectorAll ('section.hero-home [data-hero-search]');
	if (! forms.length) return;

	activeForms = [];

	forms.forEach((form) => {

		const input = form.querySelector ('input[type="search"]');
		const suggestions = form.querySelector ('.suggestions');
		if (! input || ! suggestions) {
			return;
		}

		let results = [];
		let activeIndex = -1;
		let abortController = null;
		let debounceTimer = null;

		const clearResults = () => {
			results = [];
			activeIndex = -1;
			suggestions.innerHTML = '';
			suggestions.hidden = true;
			input.setAttribute ('aria-expanded', 'false');
		};

		/*
			The markup ships with `hidden` already set on .suggestions, so this
			is a no-op on a normal page load - but ACF Blocks v3's block-preview
			pipeline (the ajax 'acf/ajax/fetch-block' response used for both the
			editor's first render and every later re-render on selection) does
			not reliably preserve a bare boolean `hidden` attribute with no
			value, only `hidden="hidden"`/`hidden=""`. Confirmed in the editor
			iframe: freshly rendered .suggestions has an empty innerHTML (JS
			never ran a search) but no `hidden` attribute at all, leaving an
			empty white dropdown box visible - the front end (a normal page
			load, not this ajax path) is unaffected. Enforcing the hidden state
			here in JS, rather than trusting the static HTML attribute to
			survive, fixes it regardless of which rendering path is in play.
		*/
		clearResults ();

		const setActive = (index) => {
			activeIndex = index;

			suggestions.querySelectorAll ('.suggestion-item').forEach((item, itemIndex) => {
				item.classList.toggle ('is-active', itemIndex === activeIndex);
				item.setAttribute ('aria-selected', itemIndex === activeIndex ? 'true' : 'false');

				if (itemIndex === activeIndex) {
					item.scrollIntoView ({
						block: 'nearest',
					});
				}
			});
		};

		const renderResults = (query) => {
			if (! results.length) {
				activeIndex = -1;
				suggestions.innerHTML = "<p class='suggestions-empty'>No matches found.</p>";
				suggestions.hidden = false;
				input.setAttribute ('aria-expanded', 'true');
				return;
			}

			suggestions.innerHTML = results.map((result, index) => {
				const label = highlightMatch (result.title, query);

				return `
					<a
						class='suggestion-item'
						href='${result.url}'
						role='option'
						data-index='${index}'
						aria-selected='false'
					>
						<span class='label'>${label}</span>
					</a>
				`;
			}).join ('');

			suggestions.hidden = false;
			input.setAttribute ('aria-expanded', 'true');
			setActive (-1);
		};

		const loadResults = async (query) => {
			if (! query) {
				clearResults ();
				return;
			}

			if (abortController) {
				abortController.abort ();
			}

			abortController = new AbortController ();

			try {
				const endpoint = window.rbkTheme?.heroSearchEndpoint;
				if (! endpoint) {
					return;
				}

				const response = await fetch (`${endpoint}?q=${encodeURIComponent (query)}`, {
					signal: abortController.signal,
				});

				if (! response.ok) {
					throw new Error ('Search request failed');
				}

				const data = await response.json ();
				results = Array.isArray (data.results) ? data.results : [];
				renderResults (query);
			} catch (error) {
				if ('AbortError' === error.name) {
					return;
				}

				clearResults ();
			}
		};

		input.addEventListener ('input', () => {
			window.clearTimeout (debounceTimer);
			debounceTimer = window.setTimeout(() => {
				loadResults (input.value.trim ());
			}, 120);
		});

		input.addEventListener ('keydown', (event) => {
			if (! suggestions.hidden && 'ArrowDown' === event.key) {
				event.preventDefault ();
				setActive (Math.min (activeIndex + 1, results.length - 1));
				return;
			}

			if (! suggestions.hidden && 'ArrowUp' === event.key) {
				event.preventDefault ();
				setActive (Math.max (activeIndex - 1, 0));
				return;
			}

			if ('Escape' === event.key) {
				clearResults ();
				return;
			}

			if ('Enter' === event.key) {
				event.preventDefault ();

				if (-1 < activeIndex && results [activeIndex]) {
					window.location.href = results [activeIndex].url;
					return;
				}

				if (results [0]) {
					window.location.href = results [0].url;
				}
			}
		});

		form.addEventListener ('submit', (event) => {
			event.preventDefault ();

			if (-1 < activeIndex && results [activeIndex]) {
				window.location.href = results [activeIndex].url;
				return;
			}

			if (results [0]) {
				window.location.href = results [0].url;
			}
		});

		suggestions.addEventListener ('mousemove', (event) => {
			const item = event.target.closest ('.suggestion-item');
			if (! item) {
				return;
			}

			setActive (parseInt (item.dataset.index, 10));
		});

		activeForms.push ({ form, clearResults });

	});

}

/*
	ACF Blocks v3 re-fetches and re-renders this block's preview markup
	inside the editor (eg. on selection/attribute change, but also shortly
	after the very first mount) without a page reload - so a single run on
	'DOMContentLoaded' isn't enough; re-run init on every preview refresh
	too, both to rewire the fresh DOM and to reapply the JS-enforced hidden
	state above (the .suggestions box would otherwise flash visible again on
	each re-render). ACF's block-preview JS runs in the outer admin window,
	while this script runs inside the iframe, so check both.
*/
const acfInstance = (typeof window !== 'undefined')
	? (window.acf || (window.parent !== window && window.parent.acf))
	: null
;

if (acfInstance) {
	acfInstance.addAction ('render_block_preview/type=hero-home', heroHomeSearch);
}
